/**
 * Subgraph flattening — rewrite `TRACE_subgraph` invocations into the standard
 * ops they stand for, producing ONE self-contained graph.
 *
 * Why: `TRACE_subgraph` is a Trace extension on a declaration. A conformant
 * KHR_interactivity viewer meets an op it does not know, backed by an
 * extension it does not implement, and cannot run the graph (#601). Flattening
 * before export keeps the emitted KHR standard-ops-only — edit-time compile to
 * core primitives (DESIGN_PRINCIPLES §4), the same move macros make.
 *
 * What makes this harder than macro expansion: a subgraph is NOT an expansion.
 * `GraphCompiler` builds a *separate* inner `FlowGraph` per definition and
 * `FlowGraphSubgraphBlock` hands each invocation its own `FlowGraphContext`, so
 * a subgraph is a closure with a private variable scope. Flattening has to
 * reproduce that scoping structurally:
 *
 * - **Variables are copied per invocation**, never shared. Two invocations of
 *   one definition get two independent copies.
 * - **Copies are renamed**, because the compiler addresses variables by NAME
 *   at runtime (`FlowGraphGetVariableBlock({ variable: prefix + name })`) while
 *   graph data addresses them by INDEX. Sharing a name would merge scopes the
 *   subgraph form kept apart.
 * - **Every inlined `variable/get` / `variable/set` gets its configuration
 *   index remapped** onto its copy. Skipping this is a silent
 *   wrong-variable read/write, not a crash — it is the bug that shipped in the
 *   editor's Inline button (trace-sandbox#194).
 *
 * Refusals leave the invocation intact rather than throwing, so a partially
 * flattenable graph still exports as much as it honestly can. They log at
 * ERROR, not WARN: `Logger.level` defaults to ERROR, and a refusal means the
 * emitted KHR still carries `TRACE_subgraph` — the exact non-conformance this
 * pass exists to remove, which must not be silent.
 *
 * - **Recursion** — cannot be flattened at all. The compiler already refuses
 *   it (`enterCompilation` → "Cycle detected"); flattening applies the same
 *   rule instead of inventing new semantics.
 * - **External-asset references** (`TRACE_subgraph.asset`) — resolving another
 *   asset's graphs is a loader concern, not a flattening one.
 * - **A body that could not itself be fully flattened** — splicing a
 *   half-flattened body would spread the refusal into the parent, so the
 *   invocation is left intact and the refusal stays contained.
 *
 * Fan-out is by nature multiplicative: a definition invoked N times is emitted
 * N times. That is inherent to flattening a closure, not a defect — but it is
 * logged, because "my graph got 12× bigger on export" should never be silent.
 */

import { GraphOps } from '../../scenedata/behaviors/behaviorgraphdata';
import { Logger } from '../../utils/Logger';
import { getBlockDef } from './blockCatalog';
import type {
    Declaration,
    GraphData,
    GraphNode,
    TypeDefinition,
} from '../../scenedata/behaviors/behaviorgraphdata';

/** Does any declaration in this graph delegate to a subgraph? */
export function hasSubgraphDeclarations(graph: GraphData): boolean {
    return graph.declarations.some(d => d.extensions?.TRACE_subgraph !== undefined);
}

// ============================================================================
// Re-collapse tags
//
// Flattening is lossy about STRUCTURE: the emitted KHR runs correctly, but a
// reimport sees a flat pile of nodes where the author had a subgraph. These
// tags are what let `recollapseSubgraphs` put the boxes back — the same
// metadata-survives-via-extras trick macros use, applied to the other
// authoring construct. The runtime ignores them; a lossy rewrite that drops
// them simply means the graph stays flat, which still executes.
// ============================================================================

/** Metadata key marking a node as a member of a flattened subgraph invocation. */
export const TRACE_SUBGRAPH_KEY = 'traceSubgraph';
/** Metadata key carrying the member's index within the original body. */
export const SUBGRAPH_INDEX_KEY = 'traceSubgraphIndex';
/** Metadata key carrying the manifest — present on body-index 0 only. */
export const SUBGRAPH_MANIFEST_KEY = 'traceSubgraphOf';

/**
 * Everything about a flattened invocation that its member nodes do not already
 * carry. Rides on the first member so one copy exists per invocation.
 *
 * The interface's node references are BODY indices, which is what makes
 * reconstruction possible: the boundary crossings can be matched back to the
 * sockets the author named, rather than regenerated as `in0`/`out0`.
 */
export interface SubgraphManifest {
    /** Declaration op of the invocation — the name the editor showed. */
    op: string;
    /**
     * How many nodes the body had. Without it a group truncated at the END
     * still looks contiguous from 0, and would rebuild a definition silently
     * missing its last nodes.
     */
    nodeCount: number;
    /** The body's interface, with node references in BODY index space. */
    iface: {
        inputFlowSockets?: GraphData['inputFlowSockets'];
        outputFlowSockets?: GraphData['outputFlowSockets'];
        inputValueSockets?: GraphData['inputValueSockets'];
        outputValueSockets?: GraphData['outputValueSockets'];
    };
    /**
     * The body's variables: their original names, and where each private copy
     * landed in the parent's table. Re-collapse pulls those copies back into
     * the definition and drops them from the parent.
     */
    variables: { name: string; parentIndex: number }[];
    /**
     * Where each body custom event (body order) landed in the parent's
     * `events`. Internal events get a private copy per invocation; an event
     * with an external id is shared (ids must be unique). Absent in manifests
     * written before custom events were inlined — read as `[]`.
     */
    events?: number[];
}

/** Index of an equal type in the table, appending it when absent. */
function typeIndex(types: TypeDefinition[], type: TypeDefinition): number {
    const existing = types.findIndex(t => t.signature === type.signature);
    if (existing !== -1) return existing;
    types.push({ ...type });
    return types.length - 1;
}

/** Index of an equal declaration in the table, appending it when absent. */
function declarationIndex(declarations: Declaration[], decl: Declaration): number {
    if (!decl.extensions) {
        const existing = declarations.findIndex(d => d.op === decl.op && !d.extensions);
        if (existing !== -1) return existing;
    }
    declarations.push(structuredClone(decl));
    return declarations.length - 1;
}

/** A name not yet taken, suffixed only as far as needed. */
function uniqueVariableName(base: string, taken: Set<string>): string {
    if (!taken.has(base)) return base;
    for (let n = 2; ; n++) {
        const candidate = `${base}__${n}`;
        if (!taken.has(candidate)) return candidate;
    }
}

/** Remap an index through a compaction map (defensive identity fallback). */
function remap(map: Map<number, number>, index: number): number {
    return map.get(index) ?? index;
}

/** Does this op declare a data input of the given name? */
function acceptsDataInput(op: string, name: string): boolean {
    return getBlockDef(op)?.dataInputs?.some(input => input.name === name) ?? false;
}

/** Where one spliced body exposes the invocation node's sockets. */
interface Splice {
    /** Flow input name → the internal node+socket it enters. */
    entry: Record<string, { node: number; socket: string }>;
    /** Flow output name → the internal node+socket that continues from it. */
    exit: Record<string, { node: number; socket: string }>;
    /** Value output name → the internal node+socket that produces it. */
    values: Record<string, { node: number; socket: string }>;
}

/** Any internal node this splice exposes — the stand-in for the dropped invocation. */
function spliceAnchor(splice: Splice): number | undefined {
    for (const group of [splice.entry, splice.values, splice.exit]) {
        const first = Object.values(group)[0];
        if (first) return first.node;
    }
    return undefined;
}

/**
 * Flatten every `TRACE_subgraph` invocation in `graph` into standard ops.
 *
 * @param graph The graph to flatten (never mutated).
 * @param allGraphs The multi-graph table `TRACE_subgraph.graph` indexes into.
 * @param stack Graph indices currently being flattened — recursion guard.
 *   Callers pass nothing; the recursion maintains it.
 * @returns A new self-contained `GraphData`, or `graph` itself when there was
 *   nothing to flatten (or nothing that could be).
 */
export function inlineSubgraphs(graph: GraphData, allGraphs: GraphData[], stack: readonly number[] = []): GraphData {
    if (!hasSubgraphDeclarations(graph)) return graph;

    const selfIndex = allGraphs.indexOf(graph);
    const activeStack = selfIndex === -1 ? stack : [...stack, selfIndex];

    const out = structuredClone(graph);
    const takenVariableNames = new Set(out.variables.map(v => v.name));
    const replacements = new Map<number, Splice>();

    const invocationCount = out.nodes.length;
    for (let i = 0; i < invocationCount; i++) {
        const invocation = out.nodes[i];
        const ref = out.declarations[invocation.declaration]?.extensions?.TRACE_subgraph;
        if (!ref) continue;

        if (ref.asset !== undefined) {
            Logger.error(
                `[inlineSubgraphs] node ${i}: external-asset subgraph (asset ${ref.asset}) cannot be flattened — exported KHR will carry TRACE_subgraph and will not run in a conformant viewer`,
            );
            continue;
        }
        if (activeStack.includes(ref.graph)) {
            Logger.error(
                `[inlineSubgraphs] node ${i}: recursive subgraph (graph ${ref.graph}) cannot be flattened — exported KHR will carry TRACE_subgraph and will not run in a conformant viewer`,
            );
            continue;
        }
        const definition = allGraphs[ref.graph];
        if (!definition) {
            Logger.error(
                `[inlineSubgraphs] node ${i}: subgraph references invalid graph index ${ref.graph} — left unflattened`,
            );
            continue;
        }

        // Depth-first: a body must be standard-ops-only before it can be
        // spliced, or the parent inherits the refusal.
        const body = inlineSubgraphs(definition, allGraphs, activeStack);
        if (hasSubgraphDeclarations(body)) {
            Logger.error(
                `[inlineSubgraphs] node ${i}: subgraph ${ref.graph} is not fully flattenable — left intact, so exported KHR will carry TRACE_subgraph`,
            );
            continue;
        }

        replacements.set(i, spliceBody(out, body, invocation, takenVariableNames, {
            op: out.declarations[invocation.declaration].op,
            instanceId: crypto.randomUUID(),
        }));
    }

    if (replacements.size === 0) return graph;

    rewireAndCompact(out, replacements);

    // A flattened graph is nobody's subgraph — its interface is meaningless
    // once there is no invoker to bridge it. Only the outermost call sheds it;
    // recursive calls return bodies whose interface is still being read.
    if (stack.length === 0) {
        delete out.inputFlowSockets;
        delete out.outputFlowSockets;
        delete out.inputValueSockets;
        delete out.outputValueSockets;
    }

    const added = out.nodes.length - graph.nodes.length;
    Logger.log(`[inlineSubgraphs] flattened ${replacements.size} invocation(s), ${added >= 0 ? '+' : ''}${added} nodes`);

    return out;
}

/**
 * Copy one subgraph body into `out`, wired to the invocation node's data
 * inputs and continuations. Returns where the invocation's sockets now live;
 * the invocation node itself is removed later by `rewireAndCompact`.
 */
function spliceBody(
    out: GraphData,
    body: GraphData,
    invocation: GraphNode,
    takenVariableNames: Set<string>,
    tag?: { op: string; instanceId: string },
): Splice {
    const typeMap = body.types.map(t => typeIndex(out.types, t));
    const declarationMap = body.declarations.map(d => declarationIndex(out.declarations, d));
    // Custom events: like variables, a private copy per invocation — an
    // internal event (no id) is the invocation's own, so two invocations of a
    // body holding an Interval never wake each other. An event with an
    // external id is shared: the spec forbids duplicate ids, and the host
    // environment addresses it by that id anyway.
    const events = (out.events ??= []);
    const eventMap = (body.events ?? []).map(e => {
        if (e.id) {
            const shared = events.findIndex(x => x.id === e.id);
            if (shared !== -1) return shared;
        }
        const copy = structuredClone(e);
        if (copy.values) {
            for (const value of Object.values(copy.values)) value.type = typeMap[value.type] ?? value.type;
        }
        events.push(copy);
        return events.length - 1;
    });

    // Variables: a private copy per invocation, renamed so the runtime (which
    // addresses by name) keeps the scopes apart.
    const variableMap = body.variables.map(v => {
        const name = uniqueVariableName(v.name, takenVariableNames);
        takenVariableNames.add(name);
        out.variables.push({ ...structuredClone(v), name, type: typeMap[v.type] ?? v.type });
        return out.variables.length - 1;
    });
    const variableManifest = body.variables.map((v, i) => ({ name: v.name, parentIndex: variableMap[i] }));

    // Pass 1 — copy nodes, remapping declarations and type indices.
    const nodeMap = body.nodes.map((node, bodyIndex) => {
        const copy = structuredClone(node);
        copy.declaration = declarationMap[node.declaration] ?? copy.declaration;
        if (tag) {
            const manifest: SubgraphManifest | undefined =
                bodyIndex === 0
                    ? {
                          op: tag.op,
                          nodeCount: body.nodes.length,
                          iface: {
                              ...(body.inputFlowSockets ? { inputFlowSockets: structuredClone(body.inputFlowSockets) } : {}),
                              ...(body.outputFlowSockets ? { outputFlowSockets: structuredClone(body.outputFlowSockets) } : {}),
                              ...(body.inputValueSockets ? { inputValueSockets: structuredClone(body.inputValueSockets) } : {}),
                              ...(body.outputValueSockets ? { outputValueSockets: structuredClone(body.outputValueSockets) } : {}),
                          },
                          variables: variableManifest,
                          ...(eventMap.length > 0 ? { events: [...eventMap] } : {}),
                      }
                    : undefined;
            copy.metadata = {
                ...copy.metadata,
                [TRACE_SUBGRAPH_KEY]: tag.instanceId,
                [SUBGRAPH_INDEX_KEY]: bodyIndex,
                ...(manifest ? { [SUBGRAPH_MANIFEST_KEY]: manifest } : {}),
            };
        }
        if (copy.configuration) {
            for (const entry of Object.values(copy.configuration)) entry.type = typeMap[entry.type] ?? entry.type;
        }
        if (copy.values) {
            for (const value of Object.values(copy.values)) {
                if ('type' in value) value.type = typeMap[value.type] ?? value.type;
            }
        }
        out.nodes.push(copy);
        return out.nodes.length - 1;
    });

    // Pass 2 — remap internal references now that every copy has an index.
    for (let bi = 0; bi < body.nodes.length; bi++) {
        const copy = out.nodes[nodeMap[bi]];
        if (copy.flows) {
            for (const flow of Object.values(copy.flows)) flow.node = nodeMap[flow.node] ?? flow.node;
        }
        if (copy.values) {
            for (const value of Object.values(copy.values)) {
                if ('node' in value) value.node = nodeMap[value.node] ?? value.node;
            }
        }
        // Variables resolve by index — an un-remapped index is a silent
        // wrong-variable access, so this is the load-bearing line.
        const op = body.declarations[body.nodes[bi].declaration]?.op;
        const variableConfig = copy.configuration?.['variable'];
        if (variableConfig && (op === GraphOps.VARIABLE_GET || op === GraphOps.VARIABLE_SET)) {
            const authored = Number(variableConfig.value[0]);
            variableConfig.value = [variableMap[authored] ?? authored];
        }
        // Same for custom events: an un-remapped index fires the parent's event.
        const eventConfig = copy.configuration?.['event'];
        if (eventConfig && (op === GraphOps.EVENT_SEND || op === GraphOps.EVENT_RECEIVE)) {
            const authored = Number(eventConfig.value[0]);
            eventConfig.value = [eventMap[authored] ?? authored];
        }
    }

    // Value inputs: the compiler binds a named input socket to every inner
    // node with a same-named data input that is unconnected or inline
    // (GraphCompiler.ts:634). Reproduce that by pushing the invocation's
    // supplied value onto those same inputs.
    for (const name of Object.keys(body.inputValueSockets ?? {})) {
        const supplied = invocation.values?.[name];
        if (!supplied) continue;
        for (let bi = 0; bi < body.nodes.length; bi++) {
            const source = body.nodes[bi];
            const existing = source.values?.[name];
            if (existing && 'node' in existing) continue; // internally wired — not an interface consumer
            const op = body.declarations[source.declaration]?.op;
            if (!op || !acceptsDataInput(op, name)) continue;
            const copy = out.nodes[nodeMap[bi]];
            (copy.values ??= {})[name] = { ...supplied };
        }
    }

    // Flow continuations: the invocation's own outbound flows attach to the
    // internal nodes the body declares as its exits.
    for (const [name, exit] of Object.entries(body.outputFlowSockets ?? {})) {
        const continuation = invocation.flows?.[name];
        if (!continuation) continue;
        const host = out.nodes[nodeMap[exit.node]];
        if (host) (host.flows ??= {})[exit.socket] = { ...continuation };
    }

    const entry: Splice['entry'] = {};
    for (const [name, mapping] of Object.entries(body.inputFlowSockets ?? {})) {
        entry[name] = { node: nodeMap[mapping.node], socket: mapping.socket };
    }
    const exit: Splice['exit'] = {};
    for (const [name, mapping] of Object.entries(body.outputFlowSockets ?? {})) {
        exit[name] = { node: nodeMap[mapping.node], socket: mapping.socket };
    }
    const values: Splice['values'] = {};
    for (const [name, mapping] of Object.entries(body.outputValueSockets ?? {})) {
        values[name] = { node: nodeMap[mapping.node], socket: mapping.socket };
    }
    return { entry, exit, values };
}

/**
 * Point every reference to a replaced invocation node at its spliced body,
 * then drop the invocation nodes and compact all indices.
 */
function rewireAndCompact(out: GraphData, replacements: Map<number, Splice>): void {
    // Inbound flow: a flow naming a socket the body does not declare falls
    // back to the body's `in`, mirroring the compiler's single-entry default.
    for (const node of out.nodes) {
        if (node.flows) {
            for (const key of Object.keys(node.flows)) {
                const target = node.flows[key];
                const splice = replacements.get(target.node);
                if (!splice) continue;
                const destination = splice.entry[target.socket] ?? splice.entry['in'];
                if (destination) {
                    node.flows[key] = { ...destination };
                } else {
                    // A flow-invoked body that declares no matching entry (and
                    // no `in`) has nowhere to land. Leaving the reference would
                    // resolve, through compaction's identity fallback, onto an
                    // unrelated node — so drop the wire rather than mis-aim it.
                    Logger.warn(
                        `[inlineSubgraphs] flow into socket "${target.socket}" has no entry point in the spliced body — wire dropped`,
                    );
                    delete node.flows[key];
                }
            }
        }
        // Inbound data: a parent reading the invocation's value output now
        // reads the internal node that produces it.
        if (node.values) {
            for (const key of Object.keys(node.values)) {
                const value = node.values[key];
                if (!('node' in value)) continue;
                const splice = replacements.get(value.node);
                if (!splice) continue;
                const source = splice.values[value.socket];
                if (source) node.values[key] = { node: source.node, socket: source.socket };
            }
        }
    }

    // This graph's OWN interface, when it is itself a subgraph being spliced
    // into someone else: a socket exposing a replaced invocation must follow
    // that invocation into the body. Compaction's identity fallback would
    // otherwise resolve the stale index onto whichever node took that slot —
    // silently wrong, and invisible for a pure-value body whose splice has no
    // flow entry to fall back on.
    for (const [name, mapping] of Object.entries(out.outputValueSockets ?? {})) {
        const splice = replacements.get(mapping.node);
        const source = splice?.values[mapping.socket];
        if (source) out.outputValueSockets![name] = { ...mapping, node: source.node, socket: source.socket };
    }
    for (const [name, mapping] of Object.entries(out.outputFlowSockets ?? {})) {
        const splice = replacements.get(mapping.node);
        const source = splice?.exit[mapping.socket];
        if (source) out.outputFlowSockets![name] = { node: source.node, socket: source.socket };
    }
    for (const [name, mapping] of Object.entries(out.inputFlowSockets ?? {})) {
        const splice = replacements.get(mapping.node);
        const destination = splice?.entry[mapping.socket] ?? splice?.entry['in'];
        if (destination) out.inputFlowSockets![name] = { node: destination.node, socket: destination.socket };
    }

    // Drop the invocation nodes and compact. A removed invocation maps to any
    // node its body exposes, so a reference the rewiring above did not cover
    // still lands inside the body rather than on a shifted neighbour.
    const indexMap = new Map<number, number>();
    let next = 0;
    for (let i = 0; i < out.nodes.length; i++) {
        if (!replacements.has(i)) indexMap.set(i, next++);
    }
    for (const [i, splice] of replacements) {
        const anchor = spliceAnchor(splice);
        if (anchor !== undefined) indexMap.set(i, remap(indexMap, anchor));
    }

    out.nodes = out.nodes.filter((_, i) => !replacements.has(i));
    for (const node of out.nodes) {
        if (node.flows) {
            for (const flow of Object.values(node.flows)) flow.node = remap(indexMap, flow.node);
        }
        if (node.values) {
            for (const value of Object.values(node.values)) {
                if ('node' in value) value.node = remap(indexMap, value.node);
            }
        }
    }
    for (const sockets of [out.inputFlowSockets, out.outputFlowSockets]) {
        if (!sockets) continue;
        for (const mapping of Object.values(sockets)) mapping.node = remap(indexMap, mapping.node);
    }
    if (out.inputValueSockets) {
        for (const socket of Object.values(out.inputValueSockets)) {
            if (socket.node !== undefined) socket.node = remap(indexMap, socket.node);
        }
    }
    if (out.outputValueSockets) {
        for (const socket of Object.values(out.outputValueSockets)) socket.node = remap(indexMap, socket.node);
    }

    // Strip declarations left unreferenced — the spliced-away subgraph
    // invocations, and any op that only they used.
    const referenced = new Set(out.nodes.map(n => n.declaration));
    const declarationMap = new Map<number, number>();
    const kept: Declaration[] = [];
    for (let i = 0; i < out.declarations.length; i++) {
        if (!referenced.has(i)) continue;
        declarationMap.set(i, kept.length);
        kept.push(out.declarations[i]);
    }
    out.declarations = kept;
    for (const node of out.nodes) node.declaration = remap(declarationMap, node.declaration);
}
